(() => {
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  if (reduceMotion) return;

  const els = Array.from(document.querySelectorAll('.shopify-section')).slice(1);
  els.forEach(el => el.classList.add('lh-reveal'));

  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0.12 });

  els.forEach(el => io.observe(el));
})();

// =========================
// L&H Gallery Drawer
// =========================
(() => {
  const GALLERY_SELECTOR = '.section-image-gallery .image-gallery-item';

  let navToken = 0;
  let navLocked = false;

  // MUST match your CSS transition duration for opacity
  const FADE_MS = 200;

  function preloadImage(url) {
    if (!url) return;
    const img = new Image();
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = url;
  }

  function getImgURL(item) {
    const img = item.querySelector('.media img');
    if (!img) return null;
    return img.currentSrc || img.src || null;
  }

  function ensureDrawer() {
    let drawer = document.getElementById('lh-galleryDrawer');
    if (drawer) return drawer;

    drawer = document.createElement('div');
    drawer.id = 'lh-galleryDrawer';
    drawer.className = 'lh-galleryDrawer';
    drawer.setAttribute('aria-hidden', 'true');

    drawer.innerHTML = `
      <div class="lh-galleryDrawer__backdrop" data-lh-close></div>

      <button class="lh-galleryDrawer__navBtn lh-galleryDrawer__navBtn--prev" type="button" data-lh-prev aria-label="Previous image">‹</button>
      <button class="lh-galleryDrawer__navBtn lh-galleryDrawer__navBtn--next" type="button" data-lh-next aria-label="Next image">›</button>

      <div class="lh-galleryDrawer__panel" role="dialog" aria-modal="true" aria-label="Gallery" tabindex="-1">
        <div class="lh-galleryDrawer__viewport" id="lhGalleryViewport">
          <div class="lh-galleryDrawer__slide" id="lhGallerySlide">
            <img id="lhGalleryImg" alt="" />
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(drawer);
    return drawer;
  }

  function isDrawerOpen() {
    const d = document.getElementById('lh-galleryDrawer');
    return !!d && d.getAttribute('aria-hidden') === 'false';
  }

  function getState(drawer) {
    const urls = drawer._lhUrls || [];
    const idx = Number.parseInt(drawer.dataset.lhIndex || '0', 10);
    return { urls, idx: Number.isFinite(idx) ? idx : 0 };
  }

  function setButtons(drawer, idx, total) {
    const prevBtn = drawer.querySelector('[data-lh-prev]');
    const nextBtn = drawer.querySelector('[data-lh-next]');
    if (prevBtn) prevBtn.disabled = idx <= 0;
    if (nextBtn) nextBtn.disabled = idx >= total - 1;
  }

  function fitPanelToImage(drawer, img, token) {
    const panel = drawer.querySelector('.lh-galleryDrawer__panel');
    const slide = drawer.querySelector('#lhGallerySlide');
    if (!panel || !slide || !img) return;

    const stillValid = () =>
      token === navToken && drawer.getAttribute('aria-hidden') === 'false';

    const applySizing = () => {
      if (!stillValid()) return;
      if (!img.naturalWidth || !img.naturalHeight) return;

      const vwCap = Math.min(window.innerWidth * 0.92, 1100);
      const vhCap = Math.min(window.innerHeight * 0.86, 900);

      const slideStyles = window.getComputedStyle(slide);
      const padX =
        (parseFloat(slideStyles.paddingLeft) || 0) + (parseFloat(slideStyles.paddingRight) || 0);
      const padY =
        (parseFloat(slideStyles.paddingTop) || 0) + (parseFloat(slideStyles.paddingBottom) || 0);

      const availableW = vwCap;
      const availableH = vhCap;
      const r = img.naturalWidth / img.naturalHeight;

      let imgW = Math.max(0, availableW - padX);
      let imgH = imgW / r;

      if (imgH > Math.max(0, availableH - padY)) {
        imgH = Math.max(0, availableH - padY);
        imgW = imgH * r;
      }

      const minW = 420;
      const minH = 320;

      const targetW = Math.max(minW, Math.min(vwCap, imgW + padX));
      const targetH = Math.max(minH, Math.min(vhCap, imgH + padY));

      requestAnimationFrame(() => {
        if (!stillValid()) return;
        panel.style.width = `${Math.round(targetW)}px`;
        panel.style.height = `${Math.round(targetH)}px`;
      });
    };

    if (!img.complete || !img.naturalWidth || !img.naturalHeight) {
      img.addEventListener('load', applySizing, { once: true });
      return;
    }

    applySizing();
  }

  function preloadNeighbors(urls, idx) {
    preloadImage(urls[idx - 1]);
    preloadImage(urls[idx + 1]);
  }

  function showIndex(drawer, idx) {
    if (navLocked) return;

    const { urls } = getState(drawer);
    const total = urls.length;
    if (!total) return;

    const clamped = Math.max(0, Math.min(idx, total - 1));
    const token = ++navToken;
    navLocked = true;

    drawer.dataset.lhIndex = String(clamped);
    setButtons(drawer, clamped, total);

    const viewport = drawer.querySelector('#lhGalleryViewport');
    const imgEl = drawer.querySelector('#lhGalleryImg');
    if (!viewport || !imgEl) {
      navLocked = false;
      return;
    }

    preloadNeighbors(urls, clamped);

    drawer.classList.add('lh-galleryDrawer--fading');
    void viewport.offsetWidth;

    window.setTimeout(() => {
      if (token !== navToken) return;

      imgEl.src = urls[clamped];
      fitPanelToImage(drawer, imgEl, token);

      requestAnimationFrame(() => {
        if (token !== navToken) return;
        drawer.classList.remove('lh-galleryDrawer--fading');

        window.setTimeout(() => {
          if (token !== navToken) return;
          navLocked = false;
        }, FADE_MS);
      });
    }, FADE_MS);
  }

  function openDrawer(items, startIndex) {
    const drawer = ensureDrawer();
    const urls = items.map(getImgURL).filter(Boolean);
    if (!urls.length) return;

    drawer._lhUrls = urls;
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';

    navToken = 0;
    navLocked = false;

    urls.forEach(preloadImage);

    requestAnimationFrame(() => {
      showIndex(drawer, startIndex);
      const panel = drawer.querySelector('.lh-galleryDrawer__panel');
      panel?.focus();
    });
  }

  function closeDrawer() {
    const drawer = document.getElementById('lh-galleryDrawer');
    if (!drawer) return;

    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';

    const panel = drawer.querySelector('.lh-galleryDrawer__panel');
    if (panel) {
      panel.style.width = '';
      panel.style.height = '';
    }

    navLocked = false;
  }

  // Open on click (prevent navigation), allow modifier click to open link normally
  document.addEventListener(
    'click',
    (e) => {
      const item = e.target.closest(GALLERY_SELECTOR);
      if (!item) return;

      if (isDrawerOpen()) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      e.preventDefault();

      const list = item.closest('.image-gallery-list');
      if (!list) return;

      const items = Array.from(list.children).filter((el) =>
        el.classList.contains('image-gallery-item')
      );

      const startIndex = items.indexOf(item);
      if (startIndex < 0) return;

      openDrawer(items, startIndex);
    },
    true
  );

  // Drawer clicks:
  // - prev/next buttons navigate
  // - clicking anywhere outside the panel closes
  document.addEventListener('click', (e) => {
    const drawer = document.getElementById('lh-galleryDrawer');
    if (!drawer || drawer.getAttribute('aria-hidden') === 'true') return;

    /* Ignore the same click that originated on a gallery tile */
    if (e.target.closest(GALLERY_SELECTOR)) return;

    if (e.target.closest('[data-lh-prev]')) {
      const { idx } = getState(drawer);
      showIndex(drawer, idx - 1);
      return;
    }

    if (e.target.closest('[data-lh-next]')) {
      const { idx } = getState(drawer);
      showIndex(drawer, idx + 1);
      return;
    }

    const panel = drawer.querySelector('.lh-galleryDrawer__panel');
    if (!panel) return;

    if (!panel.contains(e.target)) {
      closeDrawer();
    }
  });

  // Keyboard: escape closes, arrows navigate
  document.addEventListener('keydown', (e) => {
    if (!isDrawerOpen()) return;
    const drawer = document.getElementById('lh-galleryDrawer');
    if (!drawer) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      closeDrawer();
      return;
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const { idx } = getState(drawer);
      showIndex(drawer, idx - 1);
      return;
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const { idx } = getState(drawer);
      showIndex(drawer, idx + 1);
      return;
    }
  });

  // Resize: refit current image
  window.addEventListener('resize', () => {
    if (!isDrawerOpen()) return;
    const drawer = document.getElementById('lh-galleryDrawer');
    if (!drawer) return;

    const imgEl = drawer.querySelector('#lhGalleryImg');
    if (!imgEl) return;

    const token = ++navToken;
    fitPanelToImage(drawer, imgEl, token);
  });
})();